import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CreditCard, KeyRound, LifeBuoy } from 'lucide-react';
import AdminPaymentsTab from '../components/adminTabs/AdminPaymentsTab';
import PlatformPaymentsTab from '../components/adminTabs/PlatformPaymentsTab';
import { toast } from 'react-toastify';
import {
  FEATURE_KEYS,
  SUPPORT_SLA_OPTIONS,
  toggleFeatureStatus,
  setFeatureValue,
} from '../redux/featureSlice';

// Per-tab toggle config. The fekey is the source of truth — it matches the
// row in the backend Feature table and the gating call-sites in the Sidebar
// and appointment screens.
const TAB_CONFIG = {
  // Everything that shapes the ONE shared login/sign-up surface. Platform-wide
  // by nature (tenant is a dropdown on that page), so every item here is
  // superAdminOnly — a tenant admin sees the "platform-wide settings" empty
  // state instead.
  login: {
    icon: KeyRound,
    toggles: [
      // Doctor self-registration is NOT here any more (owner, 1 Oct 2026): it is
      // a per-tenant setting (auth.doctorRegistration) the Super Admin sets
      // from Tenants → open a tenant → Login Policy, beside the login
      // identifier. Patient registration stays platform-wide.
      {
        fekey: FEATURE_KEYS.authPatientRegistration,
        superAdminOnly: true,
        label: 'Patient Registration',
        description:
          "Allow new patients to sign up. When off, the patient registration card and the public CTAs that lead to it are hidden across the home page and login screen.",
      },
    ],
    // The login-identifier policy is PER HOSPITAL now (email / mobile / both +
    // the two OTP sub-options) and super-admin-managed: it is set from
    // Tenants → open a tenant → Features → "Login & registration policy",
    // never from this page — the write path here refuses those keys.
  },
  // The Doctor and Patient tabs were removed (owner, 1 Oct 2026): Medicines
  // and Family Accounts are always on, All Doctor Mode and Doctor Library are
  // retired, and All Patient Mode / the leave limit are the tenant admin's own
  // settings in the tenant web app — the Super Admin sets them per tenant from
  // Tenants → Features.
  // Support tickets all land on the Super Admin side (doc/support-single-tier-
  // plan.md), so everything here is superAdminOnly. The two toggles decide how
  // the platform team works the queues; the slider ends the SLA fallback chain:
  // doctor plan support_level → tenants.supportLevel → THIS value → '48h'.
  support: {
    icon: LifeBuoy,
    toggles: [
      {
        // Read by the backend at ticket creation (supportService
        // getOrCreateTicket → supportAgents.pickAutoAssignee). Nothing in this
        // app changes with it — the switch is the whole feature.
        fekey: FEATURE_KEYS.supportAutoAssign,
        superAdminOnly: true,
        label: 'Auto-assign new tickets',
        description:
          'New tickets go to the least-loaded platform support agent automatically. Off: every ticket waits in Unassigned until an agent accepts it. Super-admin only.',
      },
      {
        // Mirrored client-side by utils/platformPermissions.js (sidebar, route
        // guard, post-login landing) so the menu matches what the server's
        // canViewQueue will allow. Super Admin is unaffected either way.
        fekey: FEATURE_KEYS.supportQueueScoping,
        superAdminOnly: true,
        label: 'Queue-based agent access',
        description:
          'Agents see only the queues their role grants (Patient Support / Tenant Support). Off: any agent with support permissions works both queues. Super-admin only.',
      },
    ],
    sliders: [
      {
        fekey: FEATURE_KEYS.supportDefaultSla,
        superAdminOnly: true,
        label: 'Default support SLA',
        description:
          'Response-time SLA for tickets whose raiser has no plan or hospital-level SLA (MOC patients, and hospitals without a Support SLA set). Super-admin only.',
        options: SUPPORT_SLA_OPTIONS,
        defaultValue: '48h',
      },
    ],
  },
};

const ToggleSwitch = ({ checked, onChange, disabled, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    disabled={disabled}
    data-testid={testId}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-offset-2 ${
      checked ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8]' : 'bg-gray-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      aria-hidden="true"
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

// Stepped progress-bar slider for multi-value settings. Renders a filled teal
// track up to the selected stop, clickable dots at every stop, a draggable knob,
// and an invisible range input on top for pointer-drag and keyboard control.
const SegmentedSlider = ({ options, value, onChange, disabled, testId }) => {
  const found = options.findIndex((o) => o.value === value);
  const idx = found === -1 ? 0 : found;
  const lastIdx = options.length - 1;
  const pct = lastIdx === 0 ? 0 : (idx / lastIdx) * 100;

  return (
    <div className="w-full select-none" data-testid={testId}>
      <div className="relative h-6">
        {/* base track */}
        <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gray-200" />
        {/* filled progress */}
        <div
          className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition-all duration-200"
          style={{ width: `${pct}%` }}
        />
        {/* stop dots */}
        {options.map((o, i) => {
          const left = lastIdx === 0 ? 0 : (i / lastIdx) * 100;
          const reached = i <= idx;
          return (
            <button
              key={o.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(o.value)}
              data-testid={`${testId}-stop-${o.value}`}
              aria-label={o.label}
              className={`absolute top-1/2 z-10 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-white transition-colors ${
                reached ? 'border-[#4ABDBD]' : 'border-gray-300'
              } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              style={{ left: `${left}%` }}
            />
          );
        })}
        {/* knob at the selected stop */}
        <div
          className="pointer-events-none absolute top-1/2 z-20 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-md ring-2 ring-[#4ABDBD] transition-all duration-200"
          style={{ left: `${pct}%` }}
        />
        {/* invisible range for drag + keyboard */}
        <input
          type="range"
          min={0}
          max={lastIdx}
          step={1}
          value={idx}
          disabled={disabled}
          onChange={(e) => onChange(options[Number(e.target.value)].value)}
          data-testid={`${testId}-range`}
          className={`absolute inset-0 z-30 h-full w-full opacity-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        />
      </div>
      {/* stop labels */}
      <div className="relative mt-3 h-5">
        {options.map((o, i) => {
          const left = lastIdx === 0 ? 0 : (i / lastIdx) * 100;
          const active = i === idx;
          const transform =
            i === 0 ? 'translateX(0)' : i === lastIdx ? 'translateX(-100%)' : 'translateX(-50%)';
          return (
            <span
              key={o.value}
              className={`absolute top-0 whitespace-nowrap text-xs ${
                active ? 'font-semibold text-[#3AA8A8]' : 'text-gray-400'
              }`}
              style={{ left: `${left}%`, transform }}
            >
              {o.label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

const Settings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const flags = useSelector((state) => state.feature?.flags || {});
  const featureLoading = useSelector((state) => state.feature?.loading);
  const toggling = useSelector((state) => state.feature?.toggling);
  const settingValue = useSelector((state) => state.feature?.settingValue);

  // Everyone lands on the Login tab; a tenant admin sees its
  // "platform-wide settings" empty state there.
  const [activeTab, setActiveTab] = useState('login');

  // Human label for a slider stop. Options with their own labelKey (e.g. the
  // login-identifier modes) resolve it directly; leave-future-limit stops keep
  // the count-based "3 months" … / "No restriction" phrasing.
  const sliderOptionLabel = (option) => {
    if (option.labelKey) return t(option.labelKey, option.labelFallback || option.value);
    return option.months === null
      ? t('settings.leaveFutureLimit.noRestriction', 'No restriction')
      : t('settings.leaveFutureLimit.months', '{{count}} months', { count: option.months });
  };

  const handleToggle = async (fekey) => {
    const flag = flags[fekey];
    if (!flag?.featureId) {
      toast.warn(
        t(
          'settings.flagNotProvisioned',
          'This setting is not yet provisioned in the database. Re-run the seed (npm run db:seed) to create it.'
        )
      );
      return;
    }
    try {
      await dispatch(toggleFeatureStatus({ featureId: flag.featureId, fekey })).unwrap();
      toast.success(t('settings.updated', 'Setting updated'));
    } catch (err) {
      toast.error(err?.error || t('settings.updateFailed', 'Failed to update setting'));
    }
  };

  const handleSliderChange = async (fekey, value) => {
    const flag = flags[fekey];
    if (!flag?.featureId) {
      toast.warn(
        t(
          'settings.flagNotProvisioned',
          'This setting is not yet provisioned in the database. Re-run the seed (npm run db:seed) to create it.'
        )
      );
      return;
    }
    if (flag.value === value) return; // already at this stop — no-op
    try {
      await dispatch(setFeatureValue({ featureId: flag.featureId, fekey, value })).unwrap();
      toast.success(t('settings.updated', 'Setting updated'));
    } catch (err) {
      toast.error(err?.error || t('settings.updateFailed', 'Failed to update setting'));
    }
  };

  const tabs = [
    { id: 'login', label: t('settings.login', 'Login'), icon: KeyRound },
    { id: 'payments', label: t('settings.payments', 'Payments'), icon: CreditCard },
    { id: 'support', label: t('settings.support_section', 'Support'), icon: LifeBuoy },
  ];

  // 'payments' is a custom tab (rendered by AdminPaymentsTab), so it has no
  // TAB_CONFIG entry — guard against undefined.
  //
  // EACH ROLE SEES ONLY WHAT IT OWNS. There used to be a single rule — hide if
  // marked superAdminOnly — and no opposite rule, so anything unmarked showed to
  // BOTH roles. That is why Super Admin was offered "All Patient Mode" and
  // "Patient Library": nobody had marked them, not because it was intended.
  //
  //   superAdminOnly -> platform-wide (shared login/signup, patient screens):
  //                     Super Admin only.
  //   tenantOwned    -> one hospital's own switch: that hospital's admin only.
  //                     Super Admin has no hospital, so there is no row for them
  //                     to write; they set these per tenant from Tenants →
  //                     Features. Backend/config/featureOwnership.js enforces
  //                     the same split, so a hidden control is also a refused
  //                     request.
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  const ownedByCaller = (item) => {
    if (item.superAdminOnly) return isSuperAdmin;
    if (item.tenantOwned) return !isSuperAdmin;
    return true;
  };
  const currentToggles = (TAB_CONFIG[activeTab]?.toggles || []).filter(ownedByCaller);
  const currentSliders = (TAB_CONFIG[activeTab]?.sliders || []).filter(ownedByCaller);

  return (
    <div className="w-full" data-testid="settings-container">
      <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
        {/* Heading */}
        <div className="flex items-center gap-3 mb-6" data-testid="settings-header">
          {/* <div className="p-2 bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] rounded-xl shadow-md">
            <Settings2 className="w-6 h-6 text-white" />
          </div> */}
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
              data-testid="settings-title"
            >
              {t('settings.title', 'Settings')}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-testid="settings-subtitle">
              {t(
                'settings.subtitle',
                'Control which features doctors and patients see across the app.'
              )}
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="border-b border-gray-200 mb-6" data-testid="settings-tabs">
          <nav className="flex gap-2" data-testid="settings-tabs-nav">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  data-testid={`settings-tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                    isActive
                      ? 'border-[#4ABDBD] text-[#4ABDBD]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Payments is a custom admin tab; the other tabs use the toggle grid */}
        {activeTab !== 'payments' && currentToggles.length === 0 && currentSliders.length === 0 ? (
          <div data-testid="settings-no-owned-settings" className="text-center py-12 px-6">
            <p className="text-sm font-semibold text-[#1a365d]">
              {isSuperAdmin
                ? t('settings.none_for_super_admin', 'These are per-hospital settings')
                : t('settings.none_for_tenant_admin', 'These are platform-wide settings')}
            </p>
            <p className="text-xs text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
              {isSuperAdmin
                ? t('settings.none_for_super_admin_hint', 'Each hospital controls these for itself. To set them for a specific hospital, open Tenants → pick the tenant → Features.')
                : t('settings.none_for_tenant_admin_hint', 'These affect the shared sign-up pages and every patient on the platform, so they are managed centrally. Contact platform support if you need one changed.')}
            </p>
          </div>
        ) : activeTab === 'payments' ? (
          // Super Admin's payment screen is MAINTENANCE (hold a method during
          // an outage); a hospital's is its own configuration. Different
          // questions, so different components.
          isSuperAdmin ? <PlatformPaymentsTab /> : <AdminPaymentsTab />
        ) : (
        <>
        {/* Toggles for the active tab. Skipped entirely when the tab has none
            (a tenant admin on Support sees only its slider) — an empty
            bordered box reads as a bug. */}
        {currentToggles.length > 0 && (
        <div className="rounded-2xl border border-gray-200 divide-y divide-gray-100" data-testid={`settings-toggles-list-${activeTab}`}>
          {currentToggles.map((toggle) => {
            const flag = flags[toggle.fekey];
            const checked = flag?.status === 'active';
            return (
              <div
                key={toggle.fekey}
                data-testid={`settings-toggle-row-${toggle.fekey}`}
                className="flex items-start sm:items-center justify-between gap-4 p-4 sm:p-5"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-800 text-sm sm:text-base" data-testid={`settings-toggle-label-${toggle.fekey}`}>
                    {t(`settings.toggles.${toggle.fekey}.label`, toggle.label)}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1" data-testid={`settings-toggle-description-${toggle.fekey}`}>
                    {t(`settings.toggles.${toggle.fekey}.description`, toggle.description)}
                  </p>
                </div>
                <ToggleSwitch
                  checked={checked}
                  onChange={() => handleToggle(toggle.fekey)}
                  disabled={featureLoading || toggling}
                  testId={`settings-toggle-${toggle.fekey}`}
                />
              </div>
            );
          })}
        </div>
        )}

        {/* Stepped sliders for multi-value settings on the active tab */}
        {currentSliders.length > 0 && (
          <div className="mt-6 space-y-6" data-testid={`settings-sliders-list-${activeTab}`}>
            {currentSliders.map((slider) => {
              const flag = flags[slider.fekey];
              const options = slider.options.map((o) => ({
                ...o,
                label: sliderOptionLabel(o),
              }));
              const current = flag?.value ?? slider.defaultValue ?? options[options.length - 1].value;
              return (
                <div
                  key={slider.fekey}
                  data-testid={`settings-slider-row-${slider.fekey}`}
                  className="rounded-2xl border border-gray-200 p-4 sm:p-5"
                >
                  <div className="mb-5 min-w-0">
                    <div
                      className="font-semibold text-gray-800 text-sm sm:text-base"
                      data-testid={`settings-slider-label-${slider.fekey}`}
                    >
                      {t(`settings.sliders.${slider.fekey}.label`, slider.label)}
                    </div>
                    <p
                      className="text-xs sm:text-sm text-gray-500 mt-1"
                      data-testid={`settings-slider-description-${slider.fekey}`}
                    >
                      {t(`settings.sliders.${slider.fekey}.description`, slider.description)}
                    </p>
                  </div>
                  <div className="px-1 sm:px-2">
                    <SegmentedSlider
                      options={options}
                      value={current}
                      onChange={(value) => handleSliderChange(slider.fekey, value)}
                      disabled={featureLoading || settingValue}
                      testId={`settings-slider-${slider.fekey}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default Settings;
